import z from "zod";

import { updateAttributes, useUserAttributes } from "@skilitics/designer-auth/attributes";
import { Button, FieldText, FormField, FormikForm, Loading } from "@skilitics/designer-ui-components";
import type { FormatMessages } from "@skilitics/localization";

import { ButtonsWrapper, LayoutForm, LayoutGroup } from "../styled";

type FormValues = {
  given_name: string;
  family_name: string;
};

const attributeValue = z.string().max(2048);

const validationSchema = z.object({
  given_name: attributeValue.min(1),
  family_name: attributeValue.min(1),
});

const handleSubmit = async (newValues: FormValues) => {
  await updateAttributes(newValues);
};

export const AttributesForm = () => {
  // Only attempt to update attributes we know about!
  const { given_name = "", family_name = "" } = useUserAttributes() ?? {};

  return (
    <LayoutGroup>
      <FormikForm<FormValues>
        initialValues={{ given_name, family_name }}
        validationSchema={validationSchema}
        formatStrings={formatStrings}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid, isSubmitting }) => (
          <LayoutForm $gridColumns="300px 300px auto" blurOnEnter={false}>
            <FormField<FormValues> name="given_name">
              <FieldText />
            </FormField>
            <FormField<FormValues> name="family_name">
              <FieldText />
            </FormField>
            {dirty && (
              <ButtonsWrapper>
                <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                  {isSubmitting && <Loading inline size={12} />} Save
                </Button>
                <Button type="reset" color="inherit">
                  Cancel
                </Button>
              </ButtonsWrapper>
            )}
          </LayoutForm>
        )}
      </FormikForm>
    </LayoutGroup>
  );
};

const formatStrings = {
  given_nameLabel: { en: "Given Name" },
  family_nameLabel: { en: "Family Name" },
} satisfies FormatMessages;
